import React, { useState, useEffect } from 'react';
import {
  AgoraRTCProvider,
  LocalUser,
  RemoteUser,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteAudioTracks,
  useRemoteUsers,
} from 'agora-rtc-react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { generateAgoraToken, getChannelName, getUserId } from '../../utils/agora';

// Create Agora RTC client
const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

const VideoCallWithProvider = ({ 
  sessionId, 
  userId, 
  onCallEnd,
  isProvider = false 
}) => {
  return (
    <AgoraRTCProvider client={client}>
      <VideoCallContent
        sessionId={sessionId}
        userId={userId}
        onCallEnd={onCallEnd}
        isProvider={isProvider}
      />
    </AgoraRTCProvider>
  );
};

const VideoCallContent = ({ 
  sessionId, 
  userId, 
  onCallEnd,
  isProvider = false 
}) => {
  const [calling, setCalling] = useState(false);
  const [micOn, setMic] = useState(true);
  const [cameraOn, setCamera] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const appId = process.env.REACT_APP_AGORA_APP_ID;
  const channelName = getChannelName(sessionId);
  const agoraUserId = getUserId(userId);
  
  // Agora hooks
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn);
  const { localCameraTrack } = useLocalCameraTrack(cameraOn);
  const join = useJoin(
    calling ? { appid: appId, channel: channelName, token: token, uid: agoraUserId } : null,
    calling
  );
  const remoteUsers = useRemoteUsers();
  const { audioTracks } = useRemoteAudioTracks(remoteUsers);

  // Publish local tracks
  usePublish([localMicrophoneTrack, localCameraTrack]);

  // Play remote audio tracks
  useEffect(() => {
    audioTracks.map((track) => track.play());
  }, [audioTracks]);

  // Generate token on component mount
  useEffect(() => {
    const fetchToken = async () => {
      try {
        setLoading(true);
        const tokenData = await generateAgoraToken(channelName, agoraUserId);
        setToken(tokenData.token);
      } catch (error) {
        console.error('Failed to generate Agora token:', error);
        alert('Failed to generate video call token. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchToken();
  }, [channelName, agoraUserId]);

  // Call duration timer
  useEffect(() => {
    let interval;
    if (calling) {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [calling]);

  const handleJoinCall = async () => {
    try {
      setCalling(true);
    } catch (error) {
      console.error('Failed to join call:', error);
      alert('Failed to join call. Please try again.');
    }
  };

  const handleLeaveCall = () => {
    setCalling(false);
    setCallDuration(0);
    if (onCallEnd) {
      onCallEnd(callDuration);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Loading video call...</h2>
          <p>Generating secure token...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Unable to load video call</h2>
          <p>Failed to generate access token. Please refresh and try again.</p>
        </div>
      </div>
    );
  }

  if (!calling) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        padding: '2rem',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px'
      }}>
        <h2>Ready to start your session?</h2>
        <p>Session ID: {sessionId}</p>
        <p>Channel: {channelName}</p>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ marginRight: '1rem' }}>
            <input
              type="checkbox"
              checked={micOn}
              onChange={(e) => setMic(e.target.checked)}
              style={{ marginRight: '0.5rem' }}
            />
            Microphone
          </label>
          <label>
            <input
              type="checkbox"
              checked={cameraOn}
              onChange={(e) => setCamera(e.target.checked)}
              style={{ marginRight: '0.5rem' }}
            />
            Camera
          </label>
        </div>
        
        {cameraOn && localCameraTrack && (
          <div style={{ 
            width: '300px', 
            height: '200px', 
            marginBottom: '1rem',
            border: '2px solid #ddd',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <LocalUser
              cameraOn={cameraOn}
              micOn={micOn}
              videoTrack={localCameraTrack}
              audioTrack={localMicrophoneTrack}
            />
          </div>
        )}
        
        <button
          onClick={handleJoinCall}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Join Call
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#000'
    }}>
      {/* Call Header */}
      <div style={{ 
        padding: '1rem', 
        backgroundColor: '#333', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h3 style={{ margin: 0 }}>Practice Session</h3>
          <p style={{ margin: 0, fontSize: '14px' }}>Duration: {formatTime(callDuration)}</p>
        </div>
        <button
          onClick={handleLeaveCall}
          style={{
            padding: '8px 16px',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          End Call
        </button>
      </div>

      {/* Video Area */}
      <div style={{ 
        flex: 1, 
        display: 'flex',
        position: 'relative'
      }}>
        {/* Remote User Video */}
        {remoteUsers.length > 0 ? (
          <div style={{ 
            flex: 1,
            position: 'relative'
          }}>
            <RemoteUser 
              user={remoteUsers[0]} 
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        ) : (
          <div style={{ 
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '18px'
          }}>
            Waiting for other participant...
          </div>
        )}

        {/* Local User Video (Picture in Picture) */}
        <div style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          width: '200px',
          height: '150px',
          border: '2px solid #fff',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#333'
        }}>
          <LocalUser
            cameraOn={cameraOn}
            micOn={micOn}
            videoTrack={localCameraTrack}
            audioTrack={localMicrophoneTrack}
          />
        </div>
      </div>

      {/* Controls */}
      <div style={{ 
        padding: '1rem',
        backgroundColor: '#333',
        display: 'flex',
        justifyContent: 'center',
        gap: '1rem'
      }}>
        <button
          onClick={() => setMic(!micOn)}
          style={{
            padding: '12px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: micOn ? '#4CAF50' : '#f44336',
            color: 'white',
            cursor: 'pointer',
            width: '48px',
            height: '48px'
          }}
        >
          🎤
        </button>
        <button
          onClick={() => setCamera(!cameraOn)}
          style={{
            padding: '12px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: cameraOn ? '#4CAF50' : '#f44336',
            color: 'white',
            cursor: 'pointer',
            width: '48px',
            height: '48px'
          }}
        >
          📹
        </button>
      </div>
    </div>
  );
};

export default VideoCallWithProvider;